import type { ReactNode } from 'react';
import type { Mode } from '../api/types';

interface WidgetShellProps {
  mode: Mode;
  themeImageUrl?: string;
  title: string;
  subtitle?: string;
  progress: number; // 0..1
  step?: { current: number; total: number };
  children: ReactNode;
  /**
   * Quando true, NÃO aplica o background image no wrapper interno —
   * presumimos que o host (página standalone via FullScreenWidget) já
   * cobriu a viewport com o bg. Evita imagem duplicada.
   */
  fullScreen?: boolean;
}

// WidgetShell renderiza o card raiz do widget. Aplica:
// - paleta OMA clean quando mode=calc_only
// - bg-image + glassmorphism overlay quando mode=branded
//
// Sempre exibe a faixa colorida no topo + footer "Powered by OMA" no branded.
export function WidgetShell({
  mode,
  themeImageUrl,
  title,
  subtitle,
  progress,
  step,
  children,
  fullScreen,
}: WidgetShellProps) {
  const isBranded = mode === 'branded';

  // Largura aumentada (2xl → 4xl) pra acomodar a Etapa 2 do fluxo org —
  // 3 fields de combustível com unit-toggle + grid 2-col de % modais.
  const cardBase =
    'oma-calc relative w-full max-w-4xl mx-auto rounded-xl overflow-hidden shadow-md';
  const cardSurface = isBranded ? 'oma-glass' : 'bg-cream';

  const wrapperStyle =
    isBranded && themeImageUrl && !fullScreen
      ? {
          backgroundImage: `linear-gradient(rgba(41,50,62,0.18), rgba(74,76,57,0.22)), url(${themeImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : undefined;

  // Em fullScreen branded o padding vertical é maior pra dar respiro contra o bg.
  // Embedded num site host (não fullScreen) usa menos padding pra não invadir.
  const wrapperPadding = isBranded ? (fullScreen ? 'py-8 sm:py-10 md:py-12 px-4 sm:px-6' : 'py-10 px-4') : '';

  return (
    <div className={`oma-calc w-full ${wrapperPadding}`} style={wrapperStyle}>
      <article className={`${cardBase} ${cardSurface}`}>
        {/* Faixa colorida no topo */}
        <div className="oma-progress-band h-1.5 w-full" aria-hidden="true" />

        <header className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between gap-4 mb-2">
            <p className="font-accent tracking-[0.28em] text-2xs uppercase text-cobre">
              OMA · Calculadora
            </p>
            {step && (
              <span className="text-2xs uppercase tracking-widest text-tabaco">
                Passo {step.current} de {step.total}
              </span>
            )}
          </div>
          <h2 className="font-display text-2xl text-floresta leading-tight">{title}</h2>
          {subtitle && <p className="text-sm text-tabaco mt-1.5">{subtitle}</p>}
          {/* Progress bar interno */}
          <div className="mt-4 h-1 w-full bg-nevoa rounded-full overflow-hidden">
            <div
              className="h-full bg-floresta transition-[width] duration-300 ease-out"
              style={{ width: `${Math.max(0, Math.min(1, progress)) * 100}%` }}
            />
          </div>
        </header>

        <div className="px-6 pb-6">{children}</div>

        <footer className="px-6 py-3 border-t border-nevoa flex items-center justify-between text-2xs text-tabaco">
          <span>Powered by OMA · Ativos Ambientais</span>
          <a
            href="https://omagroup.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="text-cobre hover:text-cobre-700 underline-offset-2 hover:underline"
          >
            omagroup.com.br
          </a>
        </footer>
      </article>
    </div>
  );
}
